import { useEffect, useRef, useState } from "react";

import maplibregl, {
  LngLatLike,
  Map,
  RequestParameters,
  ResourceType,
  StyleSpecification
} from "maplibre-gl";

import {
  isMapboxURL,
  transformMapboxUrl
} from "maplibregl-mapbox-request-transformer";

import { useMapConfig } from "./useMapConfig";

export const DEFAULT_CENTER_INDONESIA: LngLatLike = [120.9213, -2.4];
export const TOOLTIP_OFFSET: [number, number] = [10, -10];
export const DEFAULT_ZOOM = 4;

const transformRequest = (
  url: string,
  resourceType?: ResourceType
): RequestParameters | undefined => {
  if (isMapboxURL(url)) {
    return transformMapboxUrl(
      url,
      resourceType,
      import.meta.env.VITE_MAPBOX_ACCESS_TOKEN
    );
  }

  return { url: url };
};

export const useMapInitialization = () => {
  const mapRef = useRef<Map | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const popupRef = useRef<maplibregl.Popup | null>(null);
  const baseMapRef = useRef<string | StyleSpecification | null>(null);

  const { getMapStyle } = useMapConfig();

  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [zoomLevel, setZoomLevel] = useState<number>(DEFAULT_ZOOM);
  const [currentCenter, setCurrentCenter] = useState<[number, number]>(
    DEFAULT_CENTER_INDONESIA
  );

  const setContainer = (element: HTMLDivElement) => {
    containerRef.current = element;

    if (mapRef.current && mapRef.current.getContainer() !== element) {
      mapRef.current.remove();
      mapRef.current = null;
      setIsMapLoaded(false);
    }

    if (!mapRef.current) {
      const mapOptions: maplibregl.MapOptions = {
        container: element,
        center: DEFAULT_CENTER_INDONESIA,
        zoom: DEFAULT_ZOOM,
        attributionControl: false,
        pitch: 0,
        minPitch: 0,
        maxPitch: 0,
        canvasContextAttributes: {
          preserveDrawingBuffer: true
        },
        transformRequest
      };

      mapRef.current = new maplibregl.Map(mapOptions);

      mapRef.current.setStyle(getMapStyle, {
        transformStyle: (
          previousStyle?: StyleSpecification,
          nextStyle?: StyleSpecification
        ): StyleSpecification => {
          if (!nextStyle) {
            if (!previousStyle) {
              throw new Error("Unable to resolve map style");
            }

            return previousStyle;
          }

          const sanitizedStyle: StyleSpecification = { ...nextStyle };

          delete (
            sanitizedStyle as Partial<StyleSpecification> & {
              projection?: StyleSpecification["projection"];
            }
          ).projection;

          return sanitizedStyle;
        }
      });

      baseMapRef.current = getMapStyle;
      popupRef.current = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        className: "map-tooltip",
        anchor: "bottom",
        offset: TOOLTIP_OFFSET
      });

      mapRef.current.on("load", () => setIsMapLoaded(true));

      mapRef.current.on("zoomend", () => {
        if (mapRef.current) {
          setZoomLevel(Math.floor(mapRef.current.getZoom()));
        }
      });

      mapRef.current.on("moveend", () => {
        if (mapRef.current) {
          const center = mapRef.current.getCenter();
          setCurrentCenter([center.lng, center.lat]);
        }
      });
    }
  };

  const handleMapFullScreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else document.body.requestFullscreen();
  };

  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  return {
    map: mapRef.current,
    containerRef,
    popup: popupRef.current,
    baseMapRef: baseMapRef,
    isMapLoaded,
    zoomLevel,
    setZoomLevel,
    currentCenter,
    setContainer,
    handleMapFullScreen
  };
};

export default useMapInitialization;
